import { Copy, Edit3, Eye, FileText, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Modal from '../../components/Modal';
import ConfirmModal from '../../modals/ConfirmModal';
import { fieldTypeLabel } from '../../utils/fieldTypes';

export default function FormListPage({ forms, deleteForm, duplicateForm }) {
  const [selectedForm, setSelectedForm] = useState(null);
  const [deletingForm, setDeletingForm] = useState(null);
  const [deleting, setDeleting] = useState(false);
  // Kopyalama akışı için state — kullanıcı yeni form adını girer
  const [duplicatingForm, setDuplicatingForm] = useState(null);
  const [duplicateName, setDuplicateName] = useState('');
  const [duplicating, setDuplicating] = useState(false);
  const navigate = useNavigate();

  function sectionGroups(form) {
    return form.sections?.length > 0
      ? form.sections
      : [{ id: 'default', title: 'Genel Bilgiler', description: '', fields: form.fields || [] }];
  }

  async function handleDelete() {
    setDeleting(true);
    try {
      await deleteForm(deletingForm.id);
    } finally {
      setDeleting(false);
      setDeletingForm(null);
    }
  }

  // Kopyalama modalını açar ve varsayılan ismi "{orijinal} (Kopya)" olarak doldurur
  function openDuplicate(form) {
    setDuplicatingForm(form);
    setDuplicateName(`${form.name} (Kopya)`);
  }

  async function handleDuplicate(event) {
    event?.preventDefault?.();
    if (!duplicatingForm) return;
    const trimmed = duplicateName.trim();
    if (!trimmed) return;
    setDuplicating(true);
    try {
      const ok = await duplicateForm(duplicatingForm.id, trimmed);
      if (ok) {
        setDuplicatingForm(null);
        setDuplicateName('');
      }
    } finally {
      setDuplicating(false);
    }
  }

  return (
    <section className="section page-panel">
      <div className="section-header">
        <div>
          <h2>Form Listele</h2>
          <p className="muted">Hazırlanan form şablonlarını inceleyin; düzenlemek için Düzenle butonunu kullanın.</p>
        </div>
        <span className="metric-pill">{forms.length} form</span>
      </div>

      <div className="data-list">
        {forms.map((form) => (
          <article className="data-row" key={form.id}>
            <div className="row-icon"><FileText /></div>
            <div className="row-main">
              <strong>{form.name}</strong>
              <span>{form.description || 'Açıklama yok'}</span>
            </div>
            <span className="badge">{form.fields.length} alan</span>
            <div className="button-row">
              <button className="btn btn-light" onClick={() => setSelectedForm(form)}><Eye /> Detay</button>
              <button className="btn btn-primary" onClick={() => navigate(`/admin/formlar/${form.id}/duzenle`)}><Edit3 /> Düzenle</button>
              <button className="btn btn-soft" onClick={() => openDuplicate(form)}><Copy /> Kopyala</button>
              <button className="btn btn-danger" onClick={() => setDeletingForm(form)}><Trash2 /> Sil</button>
            </div>
          </article>
        ))}
        {forms.length === 0 && <EmptyState text="Henüz form oluşturulmadı." />}
      </div>

      {selectedForm && (
        <Modal title={selectedForm.name} onClose={() => setSelectedForm(null)}>
          <div className="detail-stack">
            <p className="muted">{selectedForm.description || 'Bu form için açıklama girilmemiş.'}</p>
            <div className="field-preview-list">
              {sectionGroups(selectedForm).map((section) => (
                <div className="form-section-preview" key={section.id}>
                  <div className="form-section-heading">
                    <h3>{section.title}</h3>
                    {section.description && <p className="muted">{section.description}</p>}
                  </div>
                  {section.fields.map((field) => (
                    <div className="field-preview" key={field.id}>
                      <div>
                        <strong>{field.label}</strong>
                        <span>{field.placeholder || 'Placeholder yok'}</span>
                      </div>
                      <div className="button-row">
                        <span className="badge">{fieldTypeLabel(field.fieldType)}</span>
                        {field.isRequired && <span className="badge badge-warning">Zorunlu</span>}
                      </div>
                      {field.allowedExtensions && <span>İzinli uzantılar: {field.allowedExtensions}</span>}
                    </div>
                  ))}
                </div>
              ))}
            </div>
          </div>
        </Modal>
      )}

      <ConfirmModal
        isOpen={Boolean(deletingForm)}
        type="danger"
        title="Formu Sil"
        message={deletingForm ? `"${deletingForm.name}" formu ve bağlı tüm atamalar ile gönderimler kalıcı olarak silinecek. Bu işlem geri alınamaz.` : ''}
        confirmText={deleting ? 'Siliniyor...' : 'Evet, Sil'}
        cancelText="Vazgeç"
        onConfirm={handleDelete}
        onCancel={() => setDeletingForm(null)}
      />

      {duplicatingForm && (
        <Modal title="Formu Kopyala" onClose={() => !duplicating && setDuplicatingForm(null)}>
          <form className="detail-stack" onSubmit={handleDuplicate}>
            <p className="muted">
              <strong>"{duplicatingForm.name}"</strong> formunun tüm bölüm, alan ve seçenekleri yeni bir formla kopyalanacak.
              Atamalar ve gönderimler kopyalanmaz.
            </p>
            <div className="field">
              <label htmlFor="duplicate-form-name">Yeni form adı</label>
              <input
                id="duplicate-form-name"
                type="text"
                value={duplicateName}
                onChange={(event) => setDuplicateName(event.target.value)}
                placeholder="Yeni form adı"
                autoFocus
                required
              />
            </div>
            <div className="button-row">
              <button type="button" className="btn btn-light" onClick={() => setDuplicatingForm(null)} disabled={duplicating}>Vazgeç</button>
              <button type="submit" className="btn btn-primary" disabled={duplicating || !duplicateName.trim()}>
                <Copy /> {duplicating ? 'Kopyalanıyor...' : 'Kopyala'}
              </button>
            </div>
          </form>
        </Modal>
      )}
    </section>
  );
}

function EmptyState({ text }) {
  return <div className="empty-state">{text}</div>;
}